import React, { useEffect, useState } from "react";
import { baseUrl } from "../../config/baseUrl";
import axios from "axios";
import { Link, NavLink } from "react-router-dom";
import { format, isValid, parseISO } from "date-fns";
import EduPagination from "../edu-pagination/edu-pagination";
import ArchiveModal from "../edu-helper/archive-modal";
import UnarchiveModal from "../edu-helper/unarchive-modal";

// Helper function to safely format dates
const safeFormatDate = (dateString, formatString) => {
  if (!dateString) return "N/A";

  try {
    // Try to parse the date string
    const date = parseISO(dateString);
    if (isValid(date)) {
      return format(date, formatString);
    }
    return "Invalid Date";
  } catch (error) {
    return "Invalid Date";
  }
};

export const EduUsersListTable = ({
  userType,
  archivedVal,
  setUserData,
  setShowList,
  forceUpdate,
  setForceUpdate,
  activateEduUserEdit,
  setActivateEduUserEdit,
  editUserType,
  setEditUserType,
  tabSelected,
  setUserCount,
  setAdminCount,
  setArchiveCount,
}) => {
  const [usersList, setUsersList] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [archiveid, setArchiveid] = useState();
  const [showCampusModal, setShowCampusModal] = useState(false);
  const [selectedCampusList, setSelectedCampusList] = useState([]);

  const localUserType = JSON.parse(localStorage.getItem("user"))?.user_type;

  const archieveUser = async (userId) => {
    try {
      let response = await axios(
        `${baseUrl}/archive_user/${userId}/?is_archive=True`,
        {
          method: "PATCH",
          headers: {
            Authorization: "Token " + localStorage.getItem("token"),
          },
        }
      );
      if (response?.status === 200) {
        setForceUpdate(!forceUpdate);
      }
    } catch (error) {
      console.error("Error archiving user:", error);
    }
  };

  const unarchiveUser = async (userId) => {
    try {
      let response = await axios(
        `${baseUrl}/archive_user/${userId}/?is_archive=False`,
        {
          method: "PATCH",
          headers: {
            Authorization: "Token " + localStorage.getItem("token"),
          },
        }
      );
      if (response?.status === 200) {
        setForceUpdate(!forceUpdate);
      }
    } catch (error) {
      console.error("Error unarchiving user:", error);
    }
  };

  // Reset page number when tab changes
  useEffect(() => {
    setPageNumber(1);
  }, [tabSelected]);

  // USERS tab
  const getUserApi = async () => {
    try {
      let response = await axios(
        `${baseUrl}/get_user/?per_page=${pageLimit}&page=${pageNumber}`,
        {
          method: "GET",
          headers: {
            Authorization: "Token " + localStorage.getItem("token"),
          },
        }
      );
      if (response?.status === 200) {
        setUsersList(response?.data?.data || []);
        setTotalCount(response?.data?.count || 0);
        setUserCount(response?.data?.count || 0);
      } else {
        setUsersList([]);
        setTotalCount(0);
        setUserCount(0);
      }
    } catch (error) {
      setUsersList([]);
      setTotalCount(0);
      setUserCount(0);
    }
  };

  // ADMIN tab
  const getAdminApi = async () => {
    try {
      let apiUrl;

      // Check user type to determine which API to use
      if (
        JSON.parse(localStorage.getItem("cleaning_type")) === "College Cleaning"
      ) {
        // Use manager_detail API for COLLEGE_CLEANING user type
        apiUrl = `${baseUrl}/manager_detail/?per_page=${pageLimit}&page=${pageNumber}`;
      } else {
        // Use create_user_manager API for other education module user types
        apiUrl = `${baseUrl}/create_user_manager/?per_page=${pageLimit}&page=${pageNumber}`;
      }

      let response = await axios(apiUrl, {
        method: "GET",
        headers: {
          Authorization: "Token " + localStorage.getItem("token"),
        },
      });

      if (response?.status === 200) {
        if (
          JSON.parse(localStorage.getItem("cleaning_type")) ===
          "College Cleaning"
        ) {
          // Handle manager_detail API response (single object)
          const responseData = response?.data?.data;
          const dataArray = Array.isArray(responseData)
            ? responseData
            : [responseData];
          setUsersList(dataArray || []);
          setTotalCount(response?.data?.count || dataArray?.length || 0);
          setAdminCount(response?.data?.count || dataArray?.length || 0);
        } else {
          // Handle create_user_manager API response (array of objects)
          setUsersList(response?.data?.data || []);
          setTotalCount(response?.data?.count || 0);
          setAdminCount(response?.data?.count || 0);
        }
      } else {
        setUsersList([]);
        setTotalCount(0);
        setAdminCount(0);
      }
    } catch (error) {
      console.error("Error fetching admin data:", error);
      setUsersList([]);
      setTotalCount(0);
      setAdminCount(0);
    }
  };

  // ARCHIVED tab
  const getArchiveApi = async () => {
    try {
      let response = await axios(
        `${baseUrl}/archive_user_list/?per_page=${pageLimit}&page=${pageNumber}`,
        {
          method: "GET",
          headers: {
            Authorization: "Token " + localStorage.getItem("token"),
          },
        }
      );
      if (response?.status === 200) {
        setUsersList(response?.data?.data || []);
        setTotalCount(response?.data?.count || 0);
        setArchiveCount(response?.data?.count || 0);
      } else {
        setUsersList([]);
        setTotalCount(0);
        setArchiveCount(0);
      }
    } catch (error) {
      setUsersList([]);
      setTotalCount(0);
      setArchiveCount(0);
    }
  };

  // Fetch data for the current tab and page
  useEffect(() => {
    if (tabSelected === "user") {
      getUserApi();
    } else if (
      tabSelected === "admin" ||
      tabSelected === "cleaning_manager" ||
      tabSelected === "client"
    ) {
      // Treat cleaning_manager tab same as admin for data
      getAdminApi();
    } else if (tabSelected === "archive") {
      getArchiveApi();
    }
  }, [forceUpdate, tabSelected, pageNumber, pageLimit]);

  return (
    <div className="cards user-list">
      <div className="row">
        <div className="col-lg-12">
          <div className="table table-responsive custom-table management-table">
            <table className="table table-borderless">
              <thead>
                {tabSelected === "admin" ||
                tabSelected === "cleaning_manager" ||
                tabSelected === "client" ? (
                  <tr>
                    <th>Sr No.</th>
                    <th>First Name</th>
                    <th>User Name</th>
                    <th>User Type</th>
                    <th>Created at</th>
                    <th>Last login</th>
                    {userType === "SUPER_ADMIN" && <th>Action</th>}
                  </tr>
                ) : (
                  <tr>
                    <th>Sr No.</th>
                    <th>Name</th>
                    <th>User Name</th>
                    <th>Image</th>
                    <th>Mobile Number</th>
                    <th>Email id</th>
                    <th>Role</th>
                    <th>College</th>
                    <th>Joining Date</th>
                    <th>DoB</th>
                    <th>Action</th>
                  </tr>
                )}
              </thead>
              <tbody>
                <>
                  {usersList?.length > 0 ? (
                    <>
                      {usersList?.map((ul, idx) => {
                        if (tabSelected === "admin") {
                          return (
                            <tr key={ul?.id}>
                              <td>{++idx + 10 * (pageNumber - 1)}</td>
                              <td>
                                <span>{ul?.first_name || ul?.name}</span>
                              </td>
                              <td>
                                <span>{ul?.username}</span>
                              </td>
                              <td>
                                <span>{ul?.user_type}</span>
                              </td>
                              <td>
                                <span>
                                  {safeFormatDate(
                                    ul?.created_at || ul?.date_joined,
                                    "yyyy-MM-dd"
                                  )}
                                </span>
                              </td>
                              <td>
                                <span>
                                  {safeFormatDate(ul?.last_login, "yyyy-MM-dd")}
                                </span>
                              </td>
                              {userType === "SUPER_ADMIN" && (
                                <td>
                                  {tabSelected === "archive" ? (
                                    <Link
                                      role="button"
                                      className="btn btn-outline-success ms-2 btn-sm"
                                      data-bs-toggle="modal"
                                      data-bs-target="#unarchivemodal"
                                      onClick={() => {
                                        setArchiveid(ul?.id);
                                      }}
                                    >
                                      <i className="fa fa-cloud-download" />
                                    </Link>
                                  ) : (
                                    <>
                                      <Link
                                        role="button"
                                        className="btn btn-outline-warning ms-2 btn-sm"
                                        data-bs-toggle="modal"
                                        data-bs-target="#archivemodal"
                                        onClick={() => {
                                          setArchiveid(ul?.id);
                                        }}
                                      >
                                        <i className="fa fa-cloud-upload " />
                                      </Link>
                                      <Link
                                        role="button"
                                        className="fz-20 text-warning mx-1 p-2"
                                        data-bs-toggle="modal"
                                        data-bs-target="#edit-admin-modal"
                                        onClick={(e) => {
                                          setUserData(ul);
                                          setEditUserType("admin");
                                          setActivateEduUserEdit(true);
                                        }}
                                      >
                                        <i className="fa fa-edit " />
                                      </Link>
                                    </>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        } else {
                          return (
                            <tr key={ul?.id}>
                              <td>{++idx + 10 * (pageNumber - 1)}</td>
                              <td>
                                {ul?.first_name} {ul?.last_name}&nbsp;
                              </td>
                              <td>{ul?.username}</td>
                              <td></td>
                              <td>
                                <span>
                                  {" "}
                                  +{ul?.country_code}&nbsp;{ul?.mobile}
                                </span>
                              </td>
                              <td>
                                <span className="thm-clr">{ul?.email}</span>
                              </td>
                              <td>{ul?.user_type}</td>
                              <td className="campus-col">
                                <div
                                  className="campus-box"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    const campusList =
                                      ul?.campus_details
                                        ?.filter((cd) =>
                                          ul?.campus?.includes(cd?.id)
                                        )
                                        .map((ul) => ul?.campus_name) || [];
                                    setSelectedCampusList(campusList);
                                    setShowCampusModal(true);
                                  }}
                                  title="Click to view all campus names"
                                >
                                  {ul?.campus_details
                                    ?.filter((cd) =>
                                      ul?.campus?.includes(cd?.id)
                                    )
                                    .map((ul) => ul?.campus_name)
                                    .join(", ")}
                                </div>
                              </td>
                              <td>
                                {safeFormatDate(ul?.date_joined, "MMM dd, y")}
                              </td>
                              <td>
                                {safeFormatDate(ul?.date_of_birth, "MMM dd, y")}
                              </td>
                              <td>
                                <div className="d-flex flex-row">
                                  {tabSelected === "archive" ? (
                                    <Link
                                      role="button"
                                      className="btn btn-outline-success ms-2 btn-sm"
                                      data-bs-toggle="modal"
                                      data-bs-target="#unarchivemodal"
                                      onClick={() => {
                                        setArchiveid(ul?.id);
                                      }}
                                    >
                                      <i className="fa fa-cloud-download" />
                                    </Link>
                                  ) : (
                                    <>
                                      <Link
                                        role="button"
                                        className="btn btn-outline-warning ms-2 btn-sm"
                                        data-bs-toggle="modal"
                                        data-bs-target="#archivemodal"
                                        onClick={() => {
                                          setArchiveid(ul?.id);
                                        }}
                                      >
                                        <i className="fa fa-cloud-upload " />
                                      </Link>
                                      <Link
                                        role="button"
                                        className="btn btn-outline-warning ms-2 btn-sm"
                                        data-bs-toggle="modal"
                                        data-bs-target="#edit-user-modal"
                                        onClick={(e) => {
                                          setUserData(ul);
                                          setEditUserType("user");
                                          setActivateEduUserEdit(true);
                                        }}
                                      >
                                        <i className="fa fa-edit " />
                                      </Link>
                                    </>
                                  )}
                                </div>
                              </td>

                              {/* {archivedVal !== "Restore" && (
                                <td>
                                  <div className="d-flex flex-row">
                                    <Link
                                      className="btn btn-outline-info btn-sm"
                                      role="button"
                                      onClick={(e) => {
                                        setUserData(ul);
                                        setShowList(false);
                                      }}
                                    >
                                      <i className="fa fa-eye" />
                                    </Link>
                                  </div>
                                </td>
                              )} */}
                            </tr>
                          );
                        }
                      })}
                    </>
                  ) : (
                    <table>
                      <tr>
                        <td colSpan="4">
                          <span>No data available</span>
                        </td>
                      </tr>
                    </table>
                  )}
                </>
              </tbody>
            </table>
            <EduPagination
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              pageLimit={pageLimit}
              setPageLimit={setPageLimit}
              totalCount={totalCount}
            />
          </div>
        </div>
      </div>
      <ArchiveModal archiveHandle={archieveUser} id={archiveid} />
      <UnarchiveModal unarchiveHandle={unarchiveUser} id={archiveid} />

      {/* Campus Modal */}
      {showCampusModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.3)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Campus List</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCampusModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {selectedCampusList.length > 0 ? (
                  <ul>
                    {selectedCampusList.map((campus, idx) => (
                      <li key={idx}>{campus}</li>
                    ))}
                  </ul>
                ) : (
                  <p>No campus data available.</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCampusModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
